/**
 * Things a person has to be told, and the proof that they were.
 *
 * Two needs met by one mechanism, because the difference between them is a lifecycle rather
 * than a shape:
 *
 *   - **Terms of use.** One text per product surface, rarely changed, read before signing in
 *     and accepted once per version.
 *   - **A notice a business posts to its own people.** A Law 25 geolocation notice, a recall,
 *     an internal rule, or a recurring issue everybody keeps hitting. Written on a Tuesday
 *     afternoon by whoever noticed, and out the same day.
 *
 * What makes this worth building rather than emailing everyone is the third word in the
 * request: **trace**. An acknowledgement is only evidence if you can say *which text* somebody
 * agreed to, so a published version is immutable and an edit is a new version that has to be
 * acknowledged again. Anything else produces a record that proves nothing — and the one time
 * it matters is a grievance or an inspection, which is exactly when a record that proves
 * nothing is worse than no record at all.
 */

/**
 * What kind of thing this is.
 *
 * `kind` is the app's, and the wording is entirely the business's. Only two of these mean
 * anything to code: `geolocation`, which is the notice Quebec's Law 25 requires before a van
 * can be located, and `terms`, which is the one document that also lives on the public site so
 * it can be read before anybody signs in.
 */
export const NOTICE_KINDS = [
  'terms',
  'privacy',
  /** Employee location. Publishing one is what unlocks position reporting — see below. */
  'geolocation',
  'safety',
  'rule',
  'recall',
  'note',
] as const;
export type NoticeKind = (typeof NOTICE_KINDS)[number];

/**
 * Where a notice is shown.
 *
 * The reason this is not implied by the audience: **only the field client tracks position.** A
 * geolocation notice belongs in the van and nowhere else, and putting it in front of a
 * coordinator who never leaves the office trains everybody to click through notices without
 * reading them — which is the failure mode that makes the whole trace worthless.
 *
 * Obligation is decided by role; *presentation* is decided by surface. A notice aimed at the
 * field app is owed by the technicians it names and is never rendered in the portal.
 */
export const NOTICE_SURFACES = ['portal', 'field'] as const;
export type NoticeSurface = (typeof NOTICE_SURFACES)[number];

/**
 * How hard it stops somebody.
 *
 * Chosen per notice when it is written, because the consequence differs enormously. A
 * geolocation notice or a safety rule should stop the app until it is accepted. A recall bulletin
 * or a note about a recurring issue should not: locking a technician out of their day at seven
 * in the morning, on a roof, over a bulletin is how a business decides never to post one again.
 */
export const NOTICE_ENFORCEMENTS = ['blocking', 'acknowledge', 'informational'] as const;
export type NoticeEnforcement = (typeof NOTICE_ENFORCEMENTS)[number];

export type NoticeAudience = {
  /** Role keys. Empty means everybody who works in one of the surfaces below. */
  roles: readonly string[];
  surfaces: readonly NoticeSurface[];
};

/**
 * Does this person owe this notice?
 *
 * Two conditions, and the second one is the whole reason `surfaces` exists separately from
 * `roles`:
 *
 *   1. **The role is named**, or no roles are named at all.
 *   2. **The role's own application is one the notice is shown in.** A role says which app it
 *      signs in to (`roles.landing`), and a geolocation notice is only shown in the field app
 *      because that is the only app that reports a position.
 *
 * The second condition is not merely a filter, it is what keeps an obligation satisfiable. A
 * coordinator who owed a field-only notice could never be shown it and so could never accept
 * it, and the trace would carry a permanent, unfixable red row against their name — which is
 * how a compliance report stops being read.
 */
export function noticeApplies(
  audience: NoticeAudience,
  person: { roleKey: string; landing: NoticeSurface },
): boolean {
  if (!audience.surfaces.includes(person.landing)) return false;
  return audience.roles.length === 0 || audience.roles.includes(person.roleKey);
}

/**
 * The keys the product's own terms are published under.
 *
 * One per surface, because a technician accepting the use of a phone that holds gate codes in
 * a van is not agreeing to the same thing as a coordinator using a desktop application.
 */
export const PRODUCT_TERMS_KEYS: Record<NoticeSurface, string> = {
  portal: 'terms.portal',
  field: 'terms.field',
};

/**
 * The key the geolocation notice is published under.
 *
 * Named in code because publishing it has a consequence: it is what records that the business
 * gave its staff the notice Law 25 requires, and the field API refuses to accept a position
 * until that has happened. The wording remains entirely the business's.
 */
export const GEOLOCATION_NOTICE_KEY = 'notice.geolocation';
